/**
 * Authentication Service
 * Handles all authentication related API calls
 */

import apiClient, { withLanguageHeader } from './apiClient';
import i18n from '@/i18n';

// Import store lazily to avoid circular dependencies
let storeModule;
const getStore = async () => {
  if (!storeModule) {
    const module = await import('@/store');
    storeModule = module.default;
  }
  return storeModule;
};

// Create a separate instance for login to avoid interception
const loginClient = apiClient.create({
  withCredentials: true
});
// Instances made with create() do not inherit interceptors
loginClient.interceptors.request.use(withLanguageHeader);

const authService = {
  // Map locale to date format for settings
  _mapLocaleToDateFormat(locale) {
    switch (locale) {
      case 'en-US':
        return 'MM/DD/YYYY';
      case 'en-GB':
      case 'en-AE':
      case 'ar-AE':
      case 'ar-QA':
      case 'it-IT':
        return 'DD/MM/YYYY';
      case 'ar-SA':
        return 'DD/MM/YY';
      case 'ms-SG':
        return 'D/MM/YYYY';
      case 'ru-RU':
        return 'DD.MM.YYYY';
      case 'tr-TR':
        return 'D.MM.YYYY';
      case 'de-DE':
        return 'DD.MM.YYYY';
      case 'ja-JP':
        return 'YYYY/MM/DD';
      case 'ur-IN':
        return 'D/M/YY';
      default:
        return 'YYYY-MM-DD';
    }
  },

  /**
   * Save authentication tokens to localStorage
   * @param {Object} result - API response result
   */
  _saveAuthTokens(result) {
    if (result.tokenBearer) {
      localStorage.setItem('token', result.tokenBearer);
      localStorage.setItem('refreshToken', result.refreshToken || '');
      localStorage.setItem('userId', result.userId);
      localStorage.setItem('editionId', result.editionId || '');
      
      if (result.tenantName) {
        localStorage.setItem('tenantName', result.tenantName);
      }
    }
  },

  /**
   * Save culture/locale to localStorage and update store
   * @param {string} culture - Locale string
   */
  _saveCulture(culture) {
    if (culture) {
      localStorage.setItem('cultureLocale', culture);
      getStore().then(store => {
        try {
          const currentGeneral = store.state.settings?.general || {};
          store.commit('settings/SET_GENERAL', {
            ...currentGeneral,
            locale: culture,
            dateFormat: this._mapLocaleToDateFormat(culture)
          });
        } catch { /* Intentionally empty - ignore locale setting errors */ }
      });
    }
  },

  /**
   * Create user object from API result and credentials
   * @param {Object} result - API response result
   * @param {Object} credentials - User credentials
   * @param {Object} options - Additional options (isSuperAdmin, has2FA)
   * @returns {Object} User object
   */
  _createUserObject(result, credentials, options = {}) {
    return {
      id: result.userId,
      email: credentials.email,
      tenantId: result.tenantId || result.userId,
      editionId: result.editionId || null,
      tenantName: result.tenantName || null,
      country: result.country || null,
      city: result.city || null,
      isSuperAdmin: options.isSuperAdmin ?? false,
      ...(options.has2FA && { has2FA: true })
    };
  },
  /**
   * Check if user is super admin based on email
   * @param {string} email - User email
   * @returns {boolean} - True if email matches super admin
   */
  isSuperAdmin(email) {
    const superAdminEmail = 'supplier@nozzlesoft.com'; // Default super admin email
    return email === superAdminEmail;
  },

  /**
   * Standard login for regular users and super admin without 2FA
   * @param {Object} credentials - User credentials
   * @returns {Promise} - User data with token
   */
  login(credentials) {
    const loginData = {
      username: credentials.email,
      password: credentials.password,
      tenantId: null // Optional, if tenant-specific login is needed
    };
    
    // Add refs if provided (for encrypted registration links)
    if (credentials.refs) {
      loginData.refs = credentials.refs;
    }
    
    return loginClient.post('/api/supplier/account/login', loginData)
      .then(response => {
        const result = response.data;
        
        if (result.tokenBearer) {
          this._saveAuthTokens(result);
          this._saveCulture(result.culture);
          
          const user = this._createUserObject(result, credentials);
          localStorage.setItem('user', JSON.stringify(user));
          
          return {
            token: result.tokenBearer,
            user
          };
        }
        
        throw new Error('Invalid response from server');
      });
  },

  /**
   * Super admin login with 2FA
   * @param {Object} credentials - User credentials with 2FA code
   * @returns {Promise} - User data with token
   */
  loginWith2FA(credentials) {
    const loginData = {
      username: credentials.email,
      password: credentials.password,
      twoFactorCode: credentials.twoFactorCode,
      tenantId: null
    };
    
    return loginClient.post('/api/supplier/account/login-with-two-factor', loginData)
      .then(response => {
        const result = response.data;
        
        if (result.tokenBearer) {
          this._saveAuthTokens(result);
          this._saveCulture(result.culture);
          
          const user = this._createUserObject(result, credentials, { isSuperAdmin: true, has2FA: true });
          localStorage.setItem('user', JSON.stringify(user));
          
          return {
            token: result.tokenBearer,
            user
          };
        }
        
        throw new Error('Invalid response from server');
      });
  },

  /**
   * Get 2FA setup information (QR code and secret key)
   * @returns {Promise} - 2FA setup data
   */
  setup2FA() {
    return apiClient.post('/api/supplier/account/setup-two-factor-authentication')
      .then(response => response.data);
  },

  /**
   * Enable 2FA after verification
   * @param {string} twoFactorCode - 6-digit verification code
   * @returns {Promise} - Enable 2FA response
   */
  enable2FA(twoFactorCode) {
    return apiClient.post('/api/supplier/account/enable-two-factor-authentication', {
      twoFactorCode
    }).then(response => response.data);
  },

  /**
   * Disable 2FA for current user
   * @returns {Promise} - Disable 2FA response
   */
  disable2FA() {
    return apiClient.post('/api/supplier/account/disable-two-factor-authentication')
      .then(response => response.data);
  },

  /**
   * Verify 2FA code
   * @param {string} twoFactorCode - 6-digit verification code
   * @returns {Promise} - Verification response
   */
  verify2FA(twoFactorCode) {
    return apiClient.post('/api/supplier/account/verify-two-factor', {
      twoFactorCode
    }).then(response => response.data);
  },

  /**
   * Impersonate another user (Super Admin only)
   * @param {string} targetUsernameOrEmail - Target user to impersonate
   * @param {string} targetTenantId - Optional tenant ID
   * @returns {Promise} - Impersonation response
   */
  impersonateUser(targetUsernameOrEmail, targetTenantId = null) {
    return apiClient.post('/api/supplier/account/impersonate', {
      targetUsernameOrEmail,
      targetTenantId
    }).then(response => {
      const result = response.data;
      
      // Update stored user info with impersonation data
      if (result.tokenBearer) {
        this._saveAuthTokens(result);
        
        const currentUser = this.getCurrentUser();
        const impersonatedUser = {
          ...currentUser,
          isImpersonating: true,
          editionId: result.editionId || null, // Update editionId from API response
          tenantName: result.tenantName || null, // Add tenantName if available
          impersonatedUser: {
            id: result.userId,
            email: targetUsernameOrEmail,
            tenantId: result.tenantId,
            editionId: result.editionId || null,
            tenantName: result.tenantName || null
          }
        };
        localStorage.setItem('user', JSON.stringify(impersonatedUser));
        
        return {
          token: result.tokenBearer,
          user: impersonatedUser
        };
      }
      
      return result;
    });
  },

  /**
   * Impersonate a tenant (Super Admin only)
   * @param {string} tenantId - Target tenant ID to impersonate
   * @param {string} tenantName - Target tenant name (optional, for display)
   * @returns {Promise} - Impersonation response
   */
  impersonateTenant(tenantId, tenantName = null) {
    return apiClient.post('/api/supplier/account/impersonate-to-tenant', {
      targetTenantId: tenantId
    }).then(response => {
      const result = response.data;
      
      // Update stored user info with impersonation data
      if (result.tokenBearer) {
        this._saveAuthTokens(result);
        const currentUser = this.getCurrentUser();
        
        // Use provided tenant name or fallback to API response or tenant ID
        const displayName = tenantName || result.newTenantName || result.tenantName || i18n.global.t('admin.impersonatedTenantFallback', { id: tenantId });
        
        const impersonatedUser = {
          ...currentUser,
          isImpersonating: true,
          isImpersonatingTenant: result.isImpersonatingTenant || true,
          originalTenantId: result.originalTenantId,
          originalTenantName: result.originalTenantName,
          newTenantId: result.newTenantId || tenantId,
          newTenantName: displayName,
          tenantId: result.newTenantId || tenantId, // Update current tenant ID
          editionId: result.editionId || null, // Update editionId from API response
          impersonatedTenant: {
            id: result.newTenantId || tenantId,
            tenantId: result.newTenantId || tenantId,
            name: displayName,
            editionId: result.editionId || null
          }
        };
        localStorage.setItem('user', JSON.stringify(impersonatedUser));
        
        return {
          token: result.tokenBearer,
          user: impersonatedUser
        };
      }
      
      return result;
    });
  },

  /**
   * Stop impersonation and return to super admin session
   * @returns {Promise} - Stop impersonation response
   */
  stopImpersonation() {
    return apiClient.post('/api/supplier/account/stop-impersonation')
      .then(response => {
        const result = response.data;
        
        // Update stored user info to remove impersonation
        if (result.tokenBearer) {
          this._saveAuthTokens(result);
          const currentUser = this.getCurrentUser();
          const originalUser = {
            ...currentUser,
            isImpersonating: false,
            isImpersonatingTenant: false,
            originalTenantId: null,
            originalTenantName: null,
            newTenantId: null,
            newTenantName: null,
            tenantId: result.tenantId || currentUser.originalTenantId, // Restore original tenant
            editionId: result.editionId || null, // Restore original editionId
            impersonatedUser: null,
            impersonatedTenant: null
          };
          localStorage.setItem('user', JSON.stringify(originalUser));
          
          return {
            token: result.tokenBearer,
            user: originalUser
          };
        }
        
        return result;
      });
  },
  
  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise} - Registration response
   */
  register(userData) {
    return loginClient.post('/api/supplier/account/register', userData)
      .then(response => response.data);
  },
  
  /**
   * Logout user
   */
  logout() {
    // Clear auth data from local storage
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('editionId');
    localStorage.removeItem('user');
    
    // Optionally call API to invalidate token
    // return apiClient.post('/api/supplier/account/logout').then(response => response.data);
  },
  
  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise} - Response
   */
  requestPasswordReset(email) {
    return loginClient.post('/api/supplier/account/forgot-password', { email })
      .then(response => response.data);
  },
  
  /**
   * Reset password
   * @param {Object} resetData - Password reset data
   * @returns {Promise} - Response
   */
  resetPassword(resetData) {
    return apiClient.post('/api/supplier/account/reset-password', resetData)
      .then(response => response.data);
  },
  
  /**
   * Verify email with encrypted token
   * @param {string} encryptedToken - Encrypted token from email verification link
   * @returns {Promise} - Verification response
   */
  verifyEmail(encryptedToken) {

    
    // Send token as query parameter, not in request body
    const url = `/api/supplier/account/verify-email?encryptedToken=${encodeURIComponent(encryptedToken)}`;
    
    
    return loginClient.post(url, '', {
      headers: {
        'accept': 'text/plain'
      }
    })
      .then(response => {

        return response.data;
      })
      .catch(error => {
        console.error('AuthService - API error:', error.response?.data);
        throw error;
      });
  },
  
  /**
   * Resend email verification
   * @param {string} email - User email address
   * @returns {Promise} - Resend verification response
   */
  resendEmailVerification(email) {
    const url = `/api/supplier/account/resend-email-verification?email=${encodeURIComponent(email)}`;
    return loginClient.post(url, '', {
      headers: {
        'accept': 'text/plain'
      }
    })
      .then(response => response.data);
  },
  
  /**
   * Check if token is valid
   * @returns {Promise} - Authentication status
   */
  checkAuth() {
    const token = localStorage.getItem('token');
    
    if (!token) {
      return Promise.reject(new Error('No authentication token'));
    }
    
    // Make an API call to validate the token
    return apiClient.post('/api/supplier/account/validate-token')
      .then(() => ({ isValid: true }))
      .catch(error => {
        if (error.response && error.response.status === 401) {
          this.logout();
          throw new Error('Invalid or expired token');
        }
        return { isValid: true }; // Assume valid if server error
      });
  },
  
  /**
   * Get current user from local storage
   * @returns {Object|null} - Current user or null if not logged in
   */
  getCurrentUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  /**
   * Validate password setup token
   * @param {string} token - Password setup token
   * @returns {Promise} - Token validation response with user email
   */
  validatePasswordSetupToken(token) {
    return apiClient.post('/api/supplier/account/validate-password-setup-token', { token })
      .then(response => response.data);
  },

  /**
   * Set password for new user
   * @param {Object} passwordData - Password setup data
   * @returns {Promise} - Password setup response
   */
  setPassword(passwordData) {
    return apiClient.post('/api/supplier/account/set-password', passwordData)
      .then(response => response.data);
  },

  /**
   * Decode encrypted email from registration link
   * @param {string} unq - Encrypted email parameter
   * @param {string} key - Decryption key parameter
   * @returns {Promise} - Decoded email response
   */
  decodeRegistrationEmail(unq, key) {
    return loginClient.post('/api/supplier/account/decode-registration-email', {
      unq: unq,
      key: key
    })
      .then(response => response.data);
  },

  /**
   * Persist user's culture/locale preference
   * @param {string} userId - Current user ID
   * @param {string} culture - Locale string, e.g., 'en-US', 'tr-TR'
   * @returns {Promise}
   */
  setUserCulture(userId, culture) {
    return apiClient.post('/api/supplier/account/set-user-culture', {
      userId,
      culture
    }).then(response => response.data);
  },
};

export default authService; 